import React from 'react';
import { apis, hooks, models } from "@vote4dance/judging-sdk";
import { App, Button, Divider, Form, InputNumber, Popover, Space, Spin, Typography } from 'antd';
import { Localization, _T } from '../../../../../base-react/src/Localization';
import { EditOutlined, StopFilled, WarningFilled } from '@ant-design/icons';
var Api = apis.Api;
var Stream = hooks.streams.Stream;
var HeatsTeamResultSource = models.results.HeatsTeamResultSource;
var GroupTeamResultSource = models.results.GroupTeamResultSource;
var HeatsTeamSource = models.teams.HeatsTeamSource;
var RoundTeamSource = models.teams.RoundTeamSource;
var RoundItem = models.rounds.RoundItem;
import { JudgeType, JudgingType, RoundType } from '@vote4dance/judging-schema';
import { getNumberOfDecimals } from '../../../../../base-react/models/results';
const { Text } = Typography;
const shouldWarnWithValue = (judgeRating, teamPlacement, teamCount) => {
    const valueDifference = Math.abs(teamPlacement - judgeRating);
    return valueDifference >= teamCount / 3;
};
const safeParse = (json, fallback) => {
    try {
        return json ? JSON.parse(json) : fallback;
    }
    catch (e) {
        return fallback;
    }
};
const getPartialInputSummary = (partial, components) => {
    const componentIds = Array.isArray(components)
        ? components.map(component => component === null || component === void 0 ? void 0 : component.id).filter((id) => typeof id === 'string' && id.length > 0)
        : [];
    const keys = componentIds.length > 0
        ? componentIds
        : Object.keys(partial || {}).filter(key => key !== 'summary');
    const values = keys
        .map(key => Number(partial === null || partial === void 0 ? void 0 : partial[key]))
        .filter(value => Number.isFinite(value));
    if (!values.length) {
        const savedSummary = Number(partial === null || partial === void 0 ? void 0 : partial.summary);
        return Number.isFinite(savedSummary) ? savedSummary : null;
    }
    const sum = values.reduce((acc, current) => acc + current, 0);
    return Math.round(sum * 100) / 100;
};
export const RoundResultValue = (props) => {
    var _a, _b;
    const { round, team, judge, judging, showStatistics, states } = props;
    const [result, loading] = Stream(HeatsTeamResultSource(team.heats_team_id));
    if (loading) {
        return null;
    }
    const judge_id = judge.judge_id;
    let model = result && result.find((result) => (result.judge_id == judge_id));
    let value = model ? model.result_rating : null;
    const useRawScores = [JudgeType.ChiefOut, JudgeType.Observer].includes(judge.judge_type);
    var unjudged = !value || (round.round_type !== RoundType.Override && useRawScores);
    var displayPos = round.round_type === 'skating';
    var displayPartial = false;
    var partialSuffix = '';
    const judgingComponentsData = safeParse(round.judging_components, {});
    const partial = model ? safeParse(model.result_partial, undefined) : undefined;
    switch (judging && judging.judging_type) {
        case 'match':
            {
                if (round.judging_components) {
                    partialSuffix = '%';
                    displayPos = false;
                    displayPartial = true;
                    value = (value == 1 ? "x" : "") + (partial && Number.isFinite(partial.summary) ? ` (${partial.summary}%)` : '');
                }
                else {
                    value = value == 1 ? "x" : "";
                    unjudged = false;
                }
            }
            break;
        case 'points':
            {
                if (useRawScores) {
                    const step = ((_b = (_a = judgingComponentsData === null || judgingComponentsData === void 0 ? void 0 : judgingComponentsData.components) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.step) || 0.1;
                    const partialDefault = partial === null || partial === void 0 ? void 0 : partial.default;
                    const defaultValue = Number.isFinite(Number(partialDefault)) ? Number(partialDefault) : 0;
                    value = defaultValue.toFixed(getNumberOfDecimals(step));
                }
            }
            break;
        case 'select':
            {
                value = value == 1 ? "x" : "";
                unjudged = false;
            }
            break;
        case 'one-to-five':
            {
                value = value ? value : "5";
            }
            break;
        case 'placement':
            {
                value = value ? value : round.round_count;
            }
            break;
        case 'full':
            {
                value = value ? value : "0.00";
                displayPos = true;
            }
            break;
        case 'partial':
            {
                value = value ? value : "0.00";
                displayPos = true;
                displayPartial = true;
            }
            break;
        case 'partial-input':
            {
                const summary = getPartialInputSummary(partial, judgingComponentsData === null || judgingComponentsData === void 0 ? void 0 : judgingComponentsData.components);
                if (round.round_num_through == 0) {
                    value = (summary !== null && summary !== void 0 ? summary : '') + " / #" + (value ? value : '');
                }
                else {
                    const throughValue = value == 1 ? "x" : "-";
                    value = summary != null ? `${summary} / ${throughValue}` : throughValue;
                }
                displayPartial = true;
            }
            break;
        case 'ajs':
            {
                if (value && value.length > 0) {
                    try {
                        const rating = JSON.parse(value);
                        value = Object.values(rating)
                            .map(value => parseFloat(value))
                            .reduce((sum, value) => sum + value, 0)
                            .toFixed(getNumberOfDecimals(judgingComponentsData.components[0].step || 0.1));
                    }
                    catch (e) { }
                    displayPartial = true;
                }
            }
            break;
    }
    if (model && model.result_remarks && model.result_remarks > 0) {
        value += "*";
    }
    var position = model ? model.result_position : 0;
    if (position && position > 0 && displayPos) {
        value += " / #" + position;
    }
    const compareValue = (model === null || model === void 0 ? void 0 : model.result_position) || ((model === null || model === void 0 ? void 0 : model.result_rating) && !Number.isNaN(model.result_rating) ? parseFloat(model.result_rating) : 0);
    const shouldWarn = showStatistics && shouldWarnWithValue(compareValue, team.heats_team_placement, round.round_count);
    return React.createElement(RoundResultDropdown, { round: round, value: value, unjudged: unjudged, cancelled: team.team_cancelled > 0, model: model, displayPartial: true, partialSuffix: partialSuffix, type: shouldWarn ? 'danger' : undefined, states: states });
};
const RoundResultDropdown = (props) => {
    const { round, value, unjudged, cancelled, model, displayPartial, partialSuffix, type, states } = props;
    const settings = RoundItem.getJudgingSettings(round);
    const getCommentName = (list, id) => {
        const foundLevel = list && list.length > 0 && (list === null || list === void 0 ? void 0 : list.find((a) => a.id === id));
        return foundLevel && Localization.getLocalizedString(foundLevel.name);
    };
    const partialDropdown = () => {
        if (displayPartial) {
            const partial = model ? safeParse(model.result_partial, {}) : undefined;
            const judgingComponentsData = safeParse(round.judging_components, {});
            var mappings = judgingComponentsData ? judgingComponentsData.components : [];
            if (partial && mappings) {
                return React.createElement(Space, { orientation: 'vertical', size: 'small' },
                    mappings && mappings.map((mapping, index) => {
                        let partialValue = mapping.multiplier ? partial[mapping.id] * mapping.multiplier : partial[mapping.id];
                        if (RoundItem.usesStars(round)) {
                            // We round down as stars can be both 5 and 2 in some cases (not 2.5)
                            partialValue = Math.floor(partialValue);
                        }
                        if (round.judging_type === JudgingType.Ajs) {
                            if (states) {
                                try {
                                    const data = states[0].state_data;
                                    const teamIndex = data.teams.indexOf(model === null || model === void 0 ? void 0 : model.heats_team_id);
                                    const median = data.componentSums[teamIndex][mapping.id].m;
                                    var tolerance = round.round_tolerance || 1;
                                    var topLimitTolerance = median + tolerance;
                                    var bottomLimitTolerance = median - tolerance;
                                    const epsilon = 1e-10; // Small value to handle floating-point precision
                                    var isOutsideMedian = (partialValue < bottomLimitTolerance - epsilon) || (partialValue > topLimitTolerance + epsilon);
                                    partialValue = isOutsideMedian ? React.createElement(Text, { delete: true }, partialValue) : partialValue;
                                }
                                catch (e) { }
                            }
                        }
                        return (React.createElement(Text, { type: "secondary", key: mapping.id || `mapping-${index}` },
                            Localization.getLocalizedString(mapping.name),
                            " ",
                            React.createElement(Text, { strong: true },
                                partialValue,
                                partialSuffix)));
                    }),
                    partial.acrobatics && partial.acrobatics.length > 0 &&
                        React.createElement(React.Fragment, { key: "acrobatics-section" },
                            mappings.length > 0 && partial.acrobatics.length > 0 && React.createElement(Divider, { style: { margin: 2 } }),
                            partial.acrobatics.map((acro, index) => React.createElement(Text, { type: "secondary", key: 'acro-' + index },
                                acro.level,
                                " ",
                                React.createElement(Text, { strong: true }, acro.value)))),
                    partial.remarks && partial.remarks.length > 0 &&
                        React.createElement(React.Fragment, { key: "remarks-section" },
                            (mappings.length > 0 || partial.acrobatics.length > 0) && partial.remarks.length > 0 && React.createElement(Divider, { style: { margin: 2 } }),
                            partial.remarks.map((remark, index) => {
                                var _a;
                                return React.createElement(Text, { type: "secondary", key: 'remark-' + index },
                                    getCommentName((_a = settings.remarks) === null || _a === void 0 ? void 0 : _a.levels, remark.level),
                                    " ",
                                    React.createElement(Text, { strong: true }, remark.value));
                            })));
            }
        }
        return null;
    };
    const comment = model ? safeParse(model.result_comment, undefined) : undefined;
    const commentDropdown = () => {
        if (comment) {
            return React.createElement(Space, { orientation: 'vertical', size: 'small' }, comment.remarks && comment.remarks.map((remark, index) => {
                var _a, _b;
                return React.createElement(Text, { type: "secondary", key: 'remark-' + index },
                    remark.type ? getCommentName((_a = settings.comments) === null || _a === void 0 ? void 0 : _a.values, remark.type) : _T('remark.comment.title'),
                    " ",
                    React.createElement(Text, { strong: true }, getCommentName((_b = settings.comments) === null || _b === void 0 ? void 0 : _b.levels, remark.level)));
            }));
        }
        else {
            return null;
        }
    };
    const first = partialDropdown();
    const second = commentDropdown();
    const remarks = model && model.result_comment && safeParse(model.result_comment, {}).remarks;
    const includesWarning = remarks && remarks.some((a) => a.type == 'W');
    const includesDisqualify = remarks && remarks.some((a) => a.type == 'D');
    const content = (React.createElement(Space, null,
        React.createElement(Text, { type: type, disabled: unjudged, delete: cancelled }, value),
        includesWarning && React.createElement(WarningFilled, null),
        includesDisqualify && React.createElement(StopFilled, null)));
    if (!first && !second) {
        return content;
    }
    return (React.createElement(Popover, { content: React.createElement(Space, { orientation: 'vertical', size: 'small', separator: React.createElement(Divider, { style: { margin: 2 } }) },
            first,
            second) }, content));
};
export const RoundResultSummary = (props) => {
    const { round, team_id, judging } = props;
    const [result, loading] = Stream(RoundTeamSource(round.round_id));
    if (loading) {
        return React.createElement(Spin, null);
    }
    const childTeam = result && result.find((a) => a.team_id === team_id);
    if (childTeam) {
        var value;
        switch (judging && judging.judging_type) {
            case JudgingType.Select:
            case JudgingType.Points:
            case JudgingType.CollectStars:
                {
                    value = childTeam.heats_team_sum;
                }
                break;
            case JudgingType.Match:
                {
                    value = childTeam.heats_team_placement;
                    value = value == 1 ? "x" : "";
                }
                break;
            case JudgingType.Ajs:
                {
                    value = childTeam.heats_team_sum.toFixed(3);
                }
                break;
            default:
                {
                    value = childTeam.heats_team_placement;
                }
                break;
        }
        return React.createElement("div", null, value);
    }
    else {
        return null;
    }
};
export const RoundPointSummary = (props) => {
    const { team, label, states } = props;
    if (team.heats_team_target_usermodified > 0) {
        return null;
    }
    if (states && states.length > 0) {
        const data = states[0].state_data;
        if (data && data.componentSums && data.componentSums.length > 0) {
            const teamIndex = data.teams.indexOf(team.heats_team_id);
            if (teamIndex >= 0 && data && data.componentSums[teamIndex]) {
                var result = data.componentSums[teamIndex][label];
                if (label === 'multiplier') {
                    result = data.componentSums[teamIndex]['base'];
                    return result.m;
                }
                if (result) {
                    return result.s;
                }
            }
        }
    }
    return null;
};
export const RoundGroupSummary = (props) => {
    const { team, classItem } = props;
    const [result, loading] = Stream(GroupTeamResultSource(team.round_id, team.heats_team_id));
    const classResult = result === null || result === void 0 ? void 0 : result.find((a) => a.class_id === classItem.class_id);
    if (loading) {
        return React.createElement(Spin, null);
    }
    return classResult ? React.createElement(React.Fragment, null, classResult.heats_team_placement) : React.createElement(Text, { disabled: true }, "-");
};
export const RoundPointSummaryEdit = (props) => {
    const { team, disabled } = props;
    const [form] = Form.useForm();
    const { modal, notification } = App.useApp();
    const editSummary = () => {
        const confirmModal = modal.confirm({
            content: React.createElement(Space, { orientation: 'vertical' },
                _T('round.details.round_priority_settings.user-summary-hint'),
                React.createElement(Form, { form: form, layout: "vertical", initialValues: team },
                    React.createElement(Form.Item, { name: "heats_team_sum" },
                        React.createElement(InputNumber, { autoFocus: true, min: 0, addonAfter: _T('round.summaryShort') })))),
            onOk() {
                form
                    .validateFields()
                    .then((values) => {
                    form.resetFields();
                    confirmModal.update({
                        okButtonProps: { loading: true },
                        cancelButtonProps: { disabled: true }
                    });
                    Api.call(`${HeatsTeamSource(team.round_id, team.heat_number).url}/${team.heats_team_id}`, "override-team-summary", {
                        summary: values['heats_team_sum']
                    })
                        .then(() => {
                        confirmModal.update({
                            okButtonProps: { loading: false },
                            cancelButtonProps: { disabled: false }
                        });
                        notification.success({
                            message: _T('round.details.round_priority_settings.user-summary-success')
                        });
                    })
                        .catch((err) => {
                        confirmModal.update({
                            okButtonProps: { loading: false },
                            cancelButtonProps: { disabled: false }
                        });
                        notification.error({
                            message: _T(err.name)
                        });
                    });
                });
            }
        });
    };
    return (React.createElement(Button, { size: "small", onClick: editSummary, disabled: disabled },
        React.createElement(EditOutlined, null)));
};
